import React, { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getMapboxToken } from "../utils/mapbox";

const MAPBOX_TOKEN = getMapboxToken();

const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder,
  required,
  dataTestId,
}) => {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < 3 || !MAPBOX_TOKEN) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const encoded = encodeURIComponent(text);
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${MAPBOX_TOKEN}&country=mx&language=es&limit=5&types=address,place,locality,neighborhood,poi`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Geocoding failed");
      const data = await res.json();

      if (data.features && data.features.length > 0) {
        setSuggestions(data.features);
        setIsOpen(true);
        setActiveIndex(-1);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    } catch (err) {
      console.error("Mapbox geocoding error:", err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    if (onChange) onChange(text);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(text), 350);
  };

  const handleSelect = (feature) => {
    const address = feature.place_name;
    const [lng, lat] = feature.center;

    // Extract estado from context
    let estado = "";
    if (feature.context) {
      const regionCtx = feature.context.find((c) => c.id?.startsWith("region"));
      if (regionCtx) estado = regionCtx.text;
    }

    setQuery(address);
    setSuggestions([]);
    setIsOpen(false);

    if (onSelect) {
      onSelect({
        address,
        coordinates: [lng, lat],
        estado,
        fullFeature: feature,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const clearInput = () => {
    setQuery("");
    setSuggestions([]);
    setIsOpen(false);
    if (onChange) onChange("");
  };

  return (
    <div
      ref={wrapperRef}
      className="relative"
      data-testid={dataTestId || "address-autocomplete"}
    >
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          placeholder={placeholder || "Buscar dirección..."}
          required={required}
          className="pl-9 pr-9"
          data-testid="address-autocomplete-input"
        />
        {query && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="address-autocomplete-clear"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <div
          className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg overflow-hidden"
          data-testid="address-suggestions-dropdown"
        >
          {suggestions.map((feature, idx) => (
            <button
              key={feature.id}
              type="button"
              onClick={() => handleSelect(feature)}
              onMouseEnter={() => setActiveIndex(idx)}
              className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors text-sm ${
                idx === activeIndex
                  ? "bg-accent/10 text-accent-foreground"
                  : "hover:bg-muted/50"
              } ${idx < suggestions.length - 1 ? "border-b border-border/50" : ""}`}
              data-testid={`address-suggestion-${idx}`}
            >
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="font-medium truncate">{feature.text}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {feature.place_name}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
